import { useMemo } from "react";
import { createSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import { SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { TimeRangeTypes } from "@enums/time-range-types";
import { Assertion, RiskLevel } from "@idw-types/data/reference-data/generic";
import { WorkflowReportFraudResponse, WorkflowReportCompletionRateDataType } from "@idw-types/data/reports";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import { CardContainer } from "../../../common/card-container/card-container";
import { ContentPageBody } from "../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../common/content-page-header/content-page-header";
import { WorkflowReportFraud3DPieChart } from "./workflow-report-fraud-3d-pie-chart/workflow-report-fraud-3d-pie-chart";
import styles from "./workflow-report-fraud-container.less";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";

type WorkflowReportFraudContainerProps = {
    completionRatesResponse: WorkflowReportCompletionRateDataType;
    fraudResponse: WorkflowReportFraudResponse;
    assertions: Array<Assertion>;
    riskLevels: Array<RiskLevel>;
    searchFilters: WorkflowReportSearchFiltersState;
    isLoading: boolean;
};

export const WorkflowReportFraudContainer = (props: WorkflowReportFraudContainerProps) => {
    const { completionRatesResponse, fraudResponse, assertions, riskLevels, searchFilters, isLoading } = props;

    const { totalCount, riskCount } = useMemo(() => {
        let riskCount = 0;
        let totalCount = 0;

        if (completionRatesResponse !== null && fraudResponse !== null) {
            totalCount = completionRatesResponse.complete + completionRatesResponse.incomplete;

            fraudResponse.forEach(fraud => {
                riskCount += fraud.count;
            });
        }

        return { totalCount, riskCount };
    }, [completionRatesResponse]);

    const generateMoreDetailsLink = () => {
        const queryParametersObject: any = {
            startDate: format(startOfDay(searchFilters.startDate), SERVER_DATE_TIME_FORMAT),
            endDate: format(endOfDay(searchFilters.endDate), SERVER_DATE_TIME_FORMAT),
            timeRangeType: TimeRangeTypes.CUSTOM,
            shouldSearchOnLoad: "true",
            workflowIds: searchFilters.workflowId,
            includeArchivedServicesAndWorkflows: `${searchFilters.includeArchivedWorkflows}`
        };

        if (searchFilters.appId) {
            queryParametersObject.appId = searchFilters.appId;
        }

        if (searchFilters.country) {
            queryParametersObject.country = searchFilters.country;
        }

        const queryParameters = createSearchParams(queryParametersObject).toString();
        return `${REPORTS_TRANSACTIONS_URL}?${queryParameters}`;
    };

    const moreDetailsLink = useMemo(() => {
        return (
            <Link
                id="workflow-report-fraud-more-details-link"
                className={styles.moreDetailsLink}
                to={generateMoreDetailsLink()}
            >
                {resources.pages.workflowReport.sections.fraud.headerLink}
            </Link>
        );
    }, [searchFilters.workflowId, searchFilters.startDate, searchFilters.endDate]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowReport.sections.fraud.title} headerButton={moreDetailsLink} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && <LoadingIndicator title={resources.common.loadingIndicatorText} />}
                {(!isLoading && completionRatesResponse && fraudResponse) &&
                    <>
                        <span id="workflow-report-fraud" dangerouslySetInnerHTML={{ __html: resources.pages.workflowReport.sections.fraud.description(totalCount, riskCount) }} />
                        {riskCount > 0 && (
                            <div className={styles.graphContainer}>
                                <div className={styles.workflowReportFraudPieChartContainer}>
                                    <WorkflowReportFraud3DPieChart fraudResponse={fraudResponse} assertions={assertions} riskLevels={riskLevels} />
                                </div>
                            </div>
                        )}
                    </>
                }
            </ContentPageBody>
        </CardContainer>
    );
};